import { useCallback, useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/PhoneInput";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { usePlanLimit, PlanLimitBanner } from "@/components/PlanLimitGuard";
import { Plus, Trash2, Users2, Pencil, Layers, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";
import { ROLE_LABEL } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { getPerms } from "@/lib/permissions";
import UnitsPicker from "@/components/UnitsPicker";
import PhotoUpload from "@/components/PhotoUpload";

const ROLES = [
  { value: "company_admin", label: "Admin da Empresa" },
  { value: "manager", label: "Gestor" },
  { value: "supervisor", label: "Supervisor" },
  { value: "operator", label: "Operador" },
  { value: "auditor", label: "Auditor" },
  { value: "timeclock_kiosk", label: "Batida de Ponto (Totem)" },
];

const emptyForm = {
  name: "",
  email: "",
  password: "",
  role: "operator",
  unit_ids: [],
  sector_ids: [],
  phone: "",
  cpf: "",
  facial_photos: [],
};

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

export default function Users() {
  const { user } = useAuth();
  const perms = getPerms(user?.role);
  const isManager = user?.role === "manager";
  const limitInfo = usePlanLimit("users");
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const [newSector, setNewSector] = useState("");

  const load = useCallback(async () => {
    try {
      const [{ data: usersData }, { data: unitsData }, { data: sectorsData }] = await Promise.all([
        api.get("/users"),
        api.get("/units").catch(() => ({ data: [] })),
        api.get("/sectors").catch(() => ({ data: [] })),
      ]);
      setItems(usersData);
      setUnits(unitsData || []);
      setSectors(sectorsData || []);
    } catch (err) {
      console.error("Failed to load users", err);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const roleOptions = useMemo(
    () => (isManager ? ROLES.filter((r) => r.value !== "company_admin") : ROLES),
    [isManager]
  );
  const unitOptions = useMemo(() => {
    if (isManager && (user?.unit_ids || []).length > 0) {
      return units.filter((u) => user.unit_ids.includes(u.id));
    }
    return units;
  }, [units, isManager, user?.unit_ids]);

  const unitName = useMemo(() => {
    const map = {};
    for (const u of units) map[u.id] = u.name;
    return map;
  }, [units]);

  const sectorName = useMemo(() => {
    const map = {};
    for (const s of sectors) map[s.id] = s.name;
    return map;
  }, [sectors]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",
      role: u.role || "operator",
      unit_ids: u.unit_ids || [],
      sector_ids: u.sector_ids || [],
      phone: u.phone || "",
      cpf: u.cpf || "",
      facial_photos: u.facial_photos || [],
    });
    setOpen(true);
  };

  const canEdit = (u) =>
    perms.createUsers && !(isManager && u.role === "company_admin");

  const toggleSector = (sid) => {
    setForm((f) => {
      const cur = new Set(f.sector_ids || []);
      if (cur.has(sid)) cur.delete(sid);
      else cur.add(sid);
      return { ...f, sector_ids: Array.from(cur) };
    });
  };

  const setFacial = (idx, url) => {
    setForm((f) => {
      const arr = [...(f.facial_photos || [])];
      arr[idx] = url;
      return { ...f, facial_photos: arr };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.role !== "company_admin" && (form.unit_ids || []).length === 0) {
      toast.error("Selecione a unidade do usuário");
      return;
    }
    if (sectors.length > 0 && (form.sector_ids || []).length === 0 && form.role !== "company_admin") {
      toast.error("Selecione ao menos um setor para o usuário");
      return;
    }
    const facial = (form.facial_photos || []).filter(Boolean);
    try {
      if (editing) {
        const payload = {
          name: form.name,
          role: form.role,
          unit_ids: form.unit_ids,
          sector_ids: form.sector_ids,
          phone: form.phone,
          cpf: form.cpf,
          facial_photos: facial,
        };
        if (form.password) payload.password = form.password;
        await api.patch(`/users/${editing.id}`, payload);
        toast.success("Usuário atualizado");
      } else {
        await api.post("/users", { ...form, facial_photos: facial });
        toast.success("Usuário criado");
      }
      setOpen(false);
      setForm(emptyForm);
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao salvar");
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este usuário?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Usuário removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir");
    }
  };

  const addSector = async () => {
    if (!newSector.trim()) return;
    try {
      await api.post("/sectors", { name: newSector.trim() });
      setNewSector("");
      toast.success("Setor criado");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar setor");
    }
  };

  const removeSector = async (id) => {
    if (!confirm("Excluir este setor? Ele será removido de todos os usuários e checklists.")) return;
    try {
      await api.delete(`/sectors/${id}`);
      toast.success("Setor removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao excluir setor");
    }
  };

  const isSelfEdit = editing && editing.id === user?.id;

  return (
    <Layout
      title="Usuários"
      subtitle="Gerencie acessos, perfis e setores da equipe"
      actions={
        perms.createUsers ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setSectorsOpen(true)}
              data-testid="sectors-manage-btn"
            >
              <Layers className="h-4 w-4 mr-2" /> Setores
            </Button>
            <Button
              className="bg-emerald-800 hover:bg-emerald-900 text-white"
              data-testid={TID.usersNew}
              onClick={() => {
                if (limitInfo.reached) {
                  toast.error(
                    `Limite do plano atingido (${limitInfo.limit} usuários). Faça upgrade em Planos & Cobrança.`
                  );
                  return;
                }
                openCreate();
              }}
              disabled={limitInfo.reached}
            >
              <Plus className="h-4 w-4 mr-2" /> Novo usuário
            </Button>
          </div>
        ) : null
      }
    >
      {/* FASE 19 — sectors management */}
      <PlanLimitBanner info={limitInfo} label="usuário(s) ativo(s)" />
      <Dialog open={sectorsOpen} onOpenChange={setSectorsOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">Setores da empresa</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-stone-500 font-body -mt-2">
            Setores agrupam colaboradores (ex: Cozinha, Salão, Estoque). Checklists podem ser
            direcionados a um setor — todos os usuários do setor recebem.
          </p>
          <div className="flex gap-2">
            <Input
              value={newSector}
              onChange={(e) => setNewSector(e.target.value)}
              placeholder="Nome do setor (ex: Cozinha)"
              className="bg-white"
              data-testid="sector-name-input"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addSector();
                }
              }}
            />
            <Button
              onClick={addSector}
              className="bg-emerald-800 hover:bg-emerald-900 text-white shrink-0"
              data-testid="sector-add-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Criar
            </Button>
          </div>
          <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="sectors-list">
            {sectors.length === 0 && (
              <div className="text-sm text-stone-500 font-body text-center py-4">
                Nenhum setor cadastrado ainda.
              </div>
            )}
            {sectors.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-2 border border-stone-200 rounded-md px-3 py-2"
                data-testid={`sector-row-${s.id}`}
              >
                <Layers className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                <span className="flex-1 font-body text-sm font-semibold">{s.name}</span>
                <span className="text-xs text-stone-500 font-body">
                  {items.filter((u) => (u.sector_ids || []).includes(s.id)).length} usuário(s)
                </span>
                <button
                  type="button"
                  onClick={() => removeSector(s.id)}
                  className="text-stone-400 hover:text-rose-600"
                  data-testid={`sector-delete-${s.id}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setEditing(null);
        }}
      >
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? `Editar usuário — ${editing.name}` : "Novo usuário"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                required
                className="mt-1 bg-white"
                data-testid="user-name-input"
              />
            </div>
            <div>
              <Label>E-mail {editing ? "" : "*"}</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                required={!editing}
                disabled={!!editing}
                className={`mt-1 ${editing ? "bg-stone-50" : "bg-white"}`}
                data-testid="user-email-input"
              />
            </div>
            <div>
              <Label>{editing ? "Nova senha (opcional)" : "Senha inicial *"}</Label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => set("password", e.target.value)}
                required={!editing}
                minLength={form.password ? 6 : undefined}
                placeholder={editing ? "Deixe em branco para manter" : ""}
                className="mt-1 bg-white"
                data-testid="user-password-input"
              />
            </div>
            <div>
              <Label>Celular (WhatsApp)</Label>
              <PhoneInput
                value={form.phone}
                onChange={(v) => set("phone", v)}
                className="mt-1"
                data-testid="user-phone-input"
              />
            </div>
            <div>
              <Label>CPF</Label>
              <Input
                value={form.cpf}
                onChange={(e) => set("cpf", e.target.value)}
                placeholder="000.000.000-00"
                className="mt-1 bg-white"
                data-testid="user-cpf-input"
              />
              <p className="text-[10px] text-stone-400 font-body mt-0.5">
                Usado nas exportações de ponto AFD/AEJ (Portaria 671)
              </p>
            </div>
            <div>
              <Label>Perfil</Label>
              <Select
                value={form.role}
                onValueChange={(v) => set("role", v)}
                disabled={isSelfEdit}
              >
                <SelectTrigger className="mt-1 bg-white" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((r) => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isSelfEdit && (
                <p className="text-[11px] text-stone-500 font-body mt-1">
                  Você não pode alterar o próprio perfil.
                </p>
              )}
            </div>
            <UnitsPicker
              value={form.unit_ids}
              onChange={(v) => set("unit_ids", v)}
              units={unitOptions}
              label="Unidade do usuário"
              description={
                form.role === "company_admin"
                  ? "Admin da empresa tem acesso a todas as unidades."
                  : "A qual unidade este perfil pertencerá (obrigatório)."
              }
              allowAll={form.role === "company_admin"}
              testPrefix="user-units"
            />
            {/* FASE 19 — sectors */}
            <div className="border border-stone-200 rounded-md p-3 bg-stone-50/40">
              <Label className="font-body text-sm font-semibold text-stone-800">
                Setores do usuário
              </Label>
              <p className="text-[11px] text-stone-500 font-body mt-0.5 mb-2">
                Checklists direcionados a estes setores aparecerão para o usuário.
              </p>
              {sectors.length === 0 ? (
                <p className="text-xs text-stone-500 font-body italic">
                  Nenhum setor cadastrado — crie em "Setores" no topo da página.
                </p>
              ) : (
                <div className="space-y-0.5" data-testid="user-sectors-picker">
                  {sectors.map((s) => (
                    <label
                      key={s.id}
                      className="flex items-center gap-2 text-sm font-body cursor-pointer hover:bg-white px-2 py-1 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={(form.sector_ids || []).includes(s.id)}
                        onChange={() => toggleSector(s.id)}
                        className="accent-emerald-700"
                        data-testid={`user-sector-${s.id}`}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
            {/* CONTROLE DE PONTO — baseline facial photos */}
            <div className="border border-stone-200 rounded-md p-3 bg-stone-50/40">
              <Label className="font-body text-sm font-semibold text-stone-800">
                Reconhecimento facial (Ponto)
              </Label>              <p className="text-[11px] text-stone-500 font-body mt-0.5 mb-2">
                Cadastre até 3 fotos do rosto do colaborador. A IA compara a selfie do
                registro de ponto com estas fotos.
              </p>
              <div className="flex flex-wrap gap-3" data-testid="user-facial-photos">
                {[0, 1, 2].map((i) => (
                  <PhotoUpload
                    key={i}
                    value={(form.facial_photos || [])[i] || ""}
                    onChange={(url) => setFacial(i, url)}
                    allowGallery
                    showAiValidate={false}
                    testId={`user-facial-${i}`}
                  />
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid={TID.usersSubmit}
              >
                {editing ? "Salvar alterações" : "Criar usuário"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Card data-testid={TID.users} className="rounded-xl border-stone-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Colaborador</TableHead>
              <TableHead className="font-body">E-mail</TableHead>
              <TableHead className="font-body">Perfil</TableHead>
              <TableHead className="font-body">Setores</TableHead>
              <TableHead className="font-body">Unidades</TableHead>
              <TableHead className="font-body text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-stone-500 py-10">
                  <Users2 className="h-6 w-6 mx-auto mb-2 text-stone-400" /> Sem usuários
                </TableCell>
              </TableRow>
            )}
            {items.map((u) => (
              <TableRow key={u.id}>
                <TableCell>
                  <div className="flex items-center gap-2.5">
                    <Avatar className="h-8 w-8 shrink-0" data-testid={`user-avatar-${u.id}`}>
                      <AvatarImage src={u.picture || ""} alt={u.name} />
                      <AvatarFallback className="text-[10px] bg-emerald-700 text-white">
                        {initials(u.name)}
                      </AvatarFallback>
                    </Avatar>
                    {canEdit(u) ? (
                      <button
                        type="button"
                        onClick={() => openEdit(u)}
                        className="font-display font-semibold text-emerald-900 hover:underline inline-flex items-center gap-1.5 text-left"
                        data-testid={`user-edit-${u.id}`}
                      >
                        {u.name}
                        <Pencil className="h-3 w-3 opacity-40" />
                      </button>
                    ) : (
                      <span className="font-display font-semibold">{u.name}</span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-body text-sm">{u.email}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-emerald-700/40 text-emerald-800">
                    {ROLE_LABEL[u.role] || u.role}
                  </Badge>
                </TableCell>
                <TableCell>
                  {(u.sector_ids || []).length === 0 ? (
                    <span className="text-xs text-stone-400 font-body">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {u.sector_ids.map((sid) => (
                        <Badge key={sid} className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-[10px]">
                          {sectorName[sid] || sid.slice(0, 6)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  {(u.unit_ids || []).length === 0 ? (
                    <span className="text-xs text-stone-500 font-body">Todas</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {u.unit_ids.map((uid) => (
                        <Badge key={uid} variant="outline" className="border-stone-300 text-stone-700 text-[10px]">
                          {unitName[uid] || uid.slice(0, 6)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {perms.createUsers && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u.id)} data-testid={`user-delete-${u.id}`}>
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Layout>
  );
}
